import * as React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Radio,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { countBy, groupBy } from 'lodash';

import styles from './ParentReportPicker.module.css';

export interface ParentReportOption {
  jobId: string;
  reportId: string;
  startedAt: string;
  clientName: string;
  projectName: string;
  targetGroupName: string;
  workspaceName?: string;
  reportType?: string;
  passedQa: boolean;
}

export interface ParentReportPickerProps {
  reports: ParentReportOption[];
  selectedJobId: string | null;
  onSelect: (report: ParentReportOption) => void;
  loading?: boolean;
  error?: string;
  /** Explains why a list may be shorter than expected (e.g. M&M needs an optimization run). */
  helperText?: string;
  /**
   * Client to start filtered to, usually the user's workspace name. Ignored
   * unless it matches a report's client (ignoring case and surrounding spaces).
   */
  defaultClient?: string | null;
}

const PAGE_SIZE = 8;

const normalise = (v: string) => v.trim().toLowerCase();

const formatDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Searchable, paginated single-select table of previously run reports. */
const ParentReportPicker: React.FC<ParentReportPickerProps> = ({
  reports,
  selectedJobId,
  onSelect,
  loading,
  error,
  helperText,
  defaultClient,
}) => {
  const [query, setQuery] = React.useState('');
  const [qaOnly, setQaOnly] = React.useState(true);
  // A pick only counts for the default it was made against: switching workspace
  // brings a new default, which the list follows again. `value: null` shows every client.
  const [clientChoice, setClientChoice] = React.useState<{ forDefault?: string | null; value: string | null }>();
  const [showReportId, setShowReportId] = React.useState(false);
  const [page, setPage] = React.useState(0);

  // Clients that differ only in case or spacing ("Internal", "internal ") are one option,
  // labelled with their most common spelling.
  const clientOptions = React.useMemo(() => {
    const groups = groupBy(reports.map((r) => r.clientName).filter(Boolean), normalise);
    return Object.values(groups)
      .map((names) => Object.entries(countBy(names)).sort((a, b) => b[1] - a[1])[0][0].trim())
      .sort((a, b) => a.localeCompare(b));
  }, [reports]);
  const matchedDefault = React.useMemo(
    () =>
      (defaultClient && clientOptions.find((c) => normalise(c) === normalise(defaultClient))) ?? null,
    [clientOptions, defaultClient],
  );
  const client =
    clientChoice && clientChoice.forDefault === defaultClient ? clientChoice.value : matchedDefault;
  const setClient = (value: string | null) => setClientChoice({ forDefault: defaultClient, value });

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const wanted = client && normalise(client);
    return reports.filter(
      (r) =>
        (r.jobId === selectedJobId ||
          ((!qaOnly || r.passedQa) && (!wanted || normalise(r.clientName ?? '') === wanted))) &&
        (!q ||
          [r.clientName, r.projectName, r.targetGroupName, r.workspaceName, r.reportType, r.jobId, r.reportId]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))),
    );
  }, [reports, query, qaOnly, client, selectedJobId]);

  const activeFilters = [
    query.trim() && `“${query.trim()}”`,
    client && `client ${client}`,
    qaOnly && 'QA passed',
  ].filter(Boolean);
  const filterSummary = activeFilters.join(', ');
  const showAll = () => {
    setQuery('');
    setClient(null);
    setQaOnly(false);
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const selected = selectedJobId ? reports.find((r) => r.jobId === selectedJobId) : undefined;

  return (
    <Box className={styles.picker}>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search client, project, target group, ID"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          inputProps={{ 'aria-label': 'Search reports' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          className={styles.search}
        />
        <Autocomplete<string>
          size="small"
          options={clientOptions}
          value={client}
          onChange={(_, next) => {
            setClient(next);
            setPage(0);
          }}
          renderInput={(params) => <TextField {...params} label="Client" placeholder="All clients" />}
          className={styles.client}
        />
        <Box className={styles.switches}>
          <FormControlLabel
            control={
              <Switch
                checked={qaOnly}
                onChange={(e) => {
                  setQaOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="QA passed only"
          />
          <FormControlLabel
            control={<Switch checked={showReportId} onChange={(e) => setShowReportId(e.target.checked)} />}
            label="Show report ID"
          />
        </Box>
      </Box>
      {helperText && (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      )}
      {selected && (
        <Typography variant="body2" className={styles.selected} aria-live="polite">
          Selected: <strong>{selected.projectName}</strong> · {selected.targetGroupName} ·{' '}
          {formatDate(selected.startedAt)}
        </Typography>
      )}
      {!loading && filtered.length > 0 && filtered.length < reports.length && (
        <Typography variant="body2" color="text.secondary">
          Showing {filtered.length} of {reports.length} reports ({filterSummary}).
        </Typography>
      )}
      <TableContainer className={styles.table}>
        {loading && <LinearProgress aria-label="Loading reports" />}
        <Table size="small" aria-label="Reports">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Target group</TableCell>
              <TableCell>Report type</TableCell>
              <TableCell>Run</TableCell>
              {showReportId && <TableCell>Report ID</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE).map((r) => {
              const isSelected = !!selectedJobId && r.jobId === selectedJobId;
              return (
                <TableRow
                  key={r.reportId}
                  hover
                  selected={isSelected}
                  onClick={() => onSelect(r)}
                  className={styles.row}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={isSelected}
                      onChange={() => onSelect(r)}
                      inputProps={{ 'aria-label': `${r.projectName} – ${r.targetGroupName}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box className={styles.project}>
                      <span>{r.projectName}</span>
                      {r.passedQa && (
                        <Tooltip title="Passed QA">
                          <VerifiedIcon color="success" fontSize="inherit" aria-label="Passed QA" />
                        </Tooltip>
                      )}
                    </Box>
                    {r.workspaceName && (
                      <Typography variant="caption" color="text.secondary">
                        {r.workspaceName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.clientName}</TableCell>
                  <TableCell>{r.targetGroupName}</TableCell>
                  <TableCell>{r.reportType ?? 'Unknown'}</TableCell>
                  <TableCell>{formatDate(r.startedAt)}</TableCell>
                  {showReportId && (
                    <TableCell>
                      <code className={styles.reportId}>{r.reportId}</code>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={showReportId ? 7 : 6}>
                  <Typography variant="body2" color="text.secondary" className={styles.empty}>
                    {reports.length === 0
                      ? 'No eligible reports in this workspace.'
                      : `No reports match ${filterSummary}.`}
                  </Typography>
                  {reports.length > 0 && (
                    <Box textAlign="center">
                      <Button size="small" onClick={showAll}>
                        Show all reports
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {filtered.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={currentPage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p)}
        />
      )}
      {error && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ParentReportPicker;
